import { newLookLiftCard } from "features/portfolio/NewLook/styles.ts";
import React, { ReactNode, useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import IconWithFallback from "Global/IconWithFallback.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import {
  ConsolidatedCoinDataWithKey,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import {
  getNewLookTokens,
  getPanelShadow,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import CoinActionsDropdown from "features/portfolio/common/CoinActionsDropdown.tsx";
import usePositionItem from "features/portfolio/common/usePositionItem.tsx";
import useNewLookPositionCells from "features/portfolio/NewLook/useNewLookPositionCells.tsx";
import { NEW_LOOK_POSITION_CARD_ID } from "testIds";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  NEW_LOOK_NARROW_PHONE_QUERY,
  NEW_LOOK_PHONE_MAX_WIDTH,
} from "common/NewLook/layout.ts";
import useMedia from "utils/hooks/useMedia";

const FIGURE_MIN_FONT_SIZE = 10;
const NARROW_SPARK_WIDTH = 56;
const SKELETON_ITEM_ENDS = [false, false, true, false, false, true];

interface NewLookPositionCardProps {
  coinData: ConsolidatedCoinDataWithKey;
}

/**
 * A position as a card: the pair, its side and its 24h line on top, then
 * size, margin and P&L, then the prices, each a label over a figure.
 */
const NewLookPositionCard = ({ coinData }: NewLookPositionCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const { isDrawer } = useContext(DrawerContext) || {};
  const { isReadOnly } = useSubUserPermissions();
  const { getPrecision } = usePrecisions();
  const { getNewLookPriceTrend, openCoinExtraDetails, showEditLeverage } =
    usePositionItem({});
  const cells = useNewLookPositionCells();
  const isNarrow = useMedia([NEW_LOOK_NARROW_PHONE_QUERY], [true], false);
  const exchange = coinData.exchanges[0];
  const open = () => openCoinExtraDetails(coinData);

  const item = (label: ReactNode, value: ReactNode, end?: boolean) => (
    <Item $end={end}>
      <Label>{label}</Label>
      <AutoSizeText inheritFont={true} minFontSizePx={FIGURE_MIN_FONT_SIZE}>
        {value}
      </AutoSizeText>
    </Item>
  );

  return (
    <Card
      role="button"
      tabIndex={0}
      data-testid={NEW_LOOK_POSITION_CARD_ID}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget)
          open();
      }}
    >
      <Header>
        <Identity>
          <IconWithFallback
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={coinData.symbol}
            pxIconSize={32}
          />
          <Names>
            <Name>{coinData.symbol}</Name>
            <Side>{cells.direction(coinData)}</Side>
          </Names>
        </Identity>
        <HeaderEnd>
          {getNewLookPriceTrend(
            coinData,
            "stacked",
            isNarrow ? NARROW_SPARK_WIDTH : undefined
          )}
          {!isDrawer && !isReadOnly && (
            <CoinActionsDropdown
              coinData={coinData}
              isPosition={true}
              showEditLeverage={showEditLeverage(coinData)}
            />
          )}
        </HeaderEnd>
      </Header>
      <Grid>
        {item(t("positions.size"), cells.size(coinData, false))}
        {item(t("positions.margin"), cells.margin(coinData, false))}
        {item(t("positions.upnl"), cells.pnl(coinData), true)}
        {item(
          t("positions.entry_price"),
          <Figure>
            {coinData.entry_price
              ? getLocaleStringWithPrecision(
                  coinData.entry_price,
                  getPrecision(PrecisionType.PRICE, coinData.symbol, exchange)
                )
              : "—"}
          </Figure>
        )}
        {item(
          t("positions.price"),
          <Figure>
            <LiveSymbolPrice
              symbol={coinData.symbol}
              hideIcon={true}
              exchange={exchange}
              initialPrice={coinData.price || 0}
              fontSize={14}
              textColor={tokens.ink}
            />
          </Figure>
        )}
        {item(t("positions.liq_price"), cells.liquidation(coinData), true)}
      </Grid>
      {!isDrawer && (
        <Account>
          <IconWithFallback
            iconType={ICON_TYPES.EXCHANGE}
            coinOrExchangeName={exchange}
            pxIconSize={16}
          />
          <span>{coinData.clients[0]}</span>
        </Account>
      )}
    </Card>
  );
};

export default React.memo(NewLookPositionCard);

export const NewLookPositionCardSkeleton = () => (
  <SkeletonCard aria-hidden={true}>
    <Header>
      <Identity>
        <Skeleton width={32} height={32} radius={16} />
        <Names>
          <Skeleton width={96} height={14} />
          <Skeleton width={72} height={12} />
        </Names>
      </Identity>
      <Skeleton width={64} height={28} />
    </Header>
    <Grid>
      {SKELETON_ITEM_ENDS.map((end, index) => (
        <Item key={index} $end={end}>
          <Skeleton width={48} height={12} />
          <Skeleton width={72} height={16} />
        </Item>
      ))}
    </Grid>
    <Account>
      <Skeleton width={120} height={12} />
    </Account>
  </SkeletonCard>
);

const cardSurface = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 20px 20px 18px 24px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    padding: 14px 12px 12px 16px;
  }
`;
const Card = styled.div`
  ${cardSurface};
  cursor: pointer;
  outline: none;
  ${newLookLiftCard};
`;
const SkeletonCard = styled.div`
  ${cardSurface};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;
const HeaderEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
`;
const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Name = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Side = styled.div`
  > div {
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 12px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Item = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
  gap: 4px;
  min-width: 0;
  text-align: ${(p) => (p.$end ? "right" : "left")};
`;
const Label = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Account = styled.div`
  padding-top: 12px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
